import { useLingui } from '@lingui/react/macro'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from 'designSystem/components/DropdownMenu'
import { OutlineUnitList } from 'features/knowledgeDocumentViewer/components/toolbar/OutlineUnitList'
import { PageRangeControls } from 'features/knowledgeDocumentViewer/components/toolbar/PageRangeControls'
import type { PdfOutlineUnit } from 'features/knowledgeDocumentViewer/utils/pdfOutlineUnits'

export type PdfDownloadMenuVariant =
  | 'pdf-document'
  | 'word'
  | 'powerpoint'
  | 'spreadsheet'

type PdfDownloadMenuOptionsProps = {
  variant: PdfDownloadMenuVariant
  totalPages: number
  currentPageNumber: number
  pdfSignedUrl: string | undefined
  isDownloading: boolean
  outlineUnits: PdfOutlineUnit[]
  isOutlineLoading: boolean
  onCurrentPage: () => void
  onFullPdf: () => void
  onRange: (startPage: number, endPage: number) => void
  onOutlineUnit: (unit: PdfOutlineUnit) => void
}

export function PdfDownloadMenuOptions({
  variant,
  totalPages,
  currentPageNumber,
  pdfSignedUrl,
  isDownloading,
  outlineUnits,
  isOutlineLoading,
  onCurrentPage,
  onFullPdf,
  onRange,
  onOutlineUnit,
}: PdfDownloadMenuOptionsProps) {
  const { t } = useLingui()
  const menuItemDisabled = !pdfSignedUrl || isDownloading

  const fullPdfLabel =
    variant === 'pdf-document'
      ? t`Entire document (${totalPages} pages)`
      : t`Entire PDF (${totalPages} pages)`

  const currentItemLabel =
    variant === 'powerpoint'
      ? t`Current slide (${currentPageNumber})`
      : t`Current page (${currentPageNumber})`

  const pageRange = (
    <PageRangeControls
      totalPages={totalPages}
      pdfSignedUrl={pdfSignedUrl}
      isDownloading={isDownloading}
      onRange={onRange}
    />
  )

  const outlineLoadingPlaceholder = (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem disabled>{t`Loading…`}</DropdownMenuItem>
    </>
  )

  if (variant === 'spreadsheet') {
    return (
      <>
        <DropdownMenuItem onSelect={onFullPdf} disabled={menuItemDisabled}>
          {fullPdfLabel}
        </DropdownMenuItem>
        {isOutlineLoading
          ? outlineLoadingPlaceholder
          : outlineUnits.length > 0 && (
              <OutlineUnitList
                label={t`Sheets`}
                kind='spreadsheet'
                units={outlineUnits}
                pdfSignedUrl={pdfSignedUrl}
                isDownloading={isDownloading}
                onOutlineUnit={onOutlineUnit}
              />
            )}
      </>
    )
  }

  if (variant === 'powerpoint') {
    return (
      <>
        <DropdownMenuItem onSelect={onFullPdf} disabled={menuItemDisabled}>
          {fullPdfLabel}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onCurrentPage} disabled={menuItemDisabled}>
          {currentItemLabel}
        </DropdownMenuItem>
        {isOutlineLoading ? (
          outlineLoadingPlaceholder
        ) : outlineUnits.length > 0 ? (
          <OutlineUnitList
            label={t`Slides`}
            kind='powerpoint'
            units={outlineUnits}
            pdfSignedUrl={pdfSignedUrl}
            isDownloading={isDownloading}
            onOutlineUnit={onOutlineUnit}
          />
        ) : (
          <>
            <DropdownMenuSeparator />
            {pageRange}
          </>
        )}
      </>
    )
  }

  return (
    <>
      <DropdownMenuItem onSelect={onCurrentPage} disabled={menuItemDisabled}>
        {currentItemLabel}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {pageRange}
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onFullPdf} disabled={menuItemDisabled}>
        {fullPdfLabel}
      </DropdownMenuItem>
    </>
  )
}
